/**
 * Polyline utility functions for Google Maps
 * Decodes polyline strings and performs related operations
 */

/**
 * Decodes an encoded polyline string into an array of LatLng coordinates
 * Based on Google's Polyline Algorithm
 * https://developers.google.com/maps/documentation/utilities/polylinealgorithm
 *
 * Note: This algorithm requires bitwise operations and ++ operator
 * @param encoded Encoded polyline string
 * @returns Array of {lat, lng} coordinates
 */
/* eslint-disable no-plusplus, no-bitwise */
export const decodePolyline = (encoded: string): google.maps.LatLngLiteral[] => {
  const poly: google.maps.LatLngLiteral[] = [];
  let index = 0;
  const len = encoded.length;
  let lat = 0;
  let lng = 0;

  while (index < len) {
    let b: number;
    let shift = 0;
    let result = 0;

    // Decode latitude
    do {
      b = encoded.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);

    const dlat = (result & 1) !== 0 ? ~(result >> 1) : result >> 1;
    lat += dlat;

    shift = 0;
    result = 0;

    // Decode longitude
    do {
      b = encoded.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);

    const dlng = (result & 1) !== 0 ? ~(result >> 1) : result >> 1;
    lng += dlng;

    poly.push({
      lat: lat / 1e5,
      lng: lng / 1e5,
    });
  }

  return poly;
};
/* eslint-enable no-plusplus, no-bitwise */

/**
 * Converts decoded polyline to Google Maps LatLng objects
 * @param encoded Encoded polyline string
 * @returns Array of google.maps.LatLng objects
 */
export const decodePolylineToLatLng = (encoded: string): google.maps.LatLng[] => {
  const decoded = decodePolyline(encoded);
  return decoded.map((coord) => new google.maps.LatLng(coord.lat, coord.lng));
};

/**
 * Calculates bounds from a polyline string
 * @param encoded Encoded polyline string
 * @returns Google Maps LatLngBounds object
 */
export const getBoundsFromPolyline = (encoded: string): google.maps.LatLngBounds => {
  const bounds = new google.maps.LatLngBounds();
  const decoded = decodePolyline(encoded);

  decoded.forEach((coord) => {
    bounds.extend(coord);
  });

  return bounds;
};

/**
 * Finds the closest point on a polyline to a given coordinate
 * Used for placing markers/POIs along the route
 *
 * @param polyline Decoded polyline coordinates
 * @param targetPoint Target coordinate to find closest point to
 * @returns Index of closest point in polyline array
 */
export const findClosestPointOnPolyline = (
  polyline: google.maps.LatLngLiteral[],
  targetPoint: google.maps.LatLngLiteral,
): number => {
  let minDistance = Infinity;
  let closestIndex = 0;

  polyline.forEach((point, index) => {
    const distance = getDistanceBetweenPoints(point, targetPoint);
    if (distance < minDistance) {
      minDistance = distance;
      closestIndex = index;
    }
  });

  return closestIndex;
};

/**
 * Calculates approximate distance between two points using Haversine formula
 * @param point1 First coordinate
 * @param point2 Second coordinate
 * @returns Distance in meters
 */
const getDistanceBetweenPoints = (point1: google.maps.LatLngLiteral, point2: google.maps.LatLngLiteral): number => {
  const R = 6371e3; // Earth's radius in meters
  const φ1 = (point1.lat * Math.PI) / 180;
  const φ2 = (point2.lat * Math.PI) / 180;
  const Δφ = ((point2.lat - point1.lat) * Math.PI) / 180;
  const Δλ = ((point2.lng - point1.lng) * Math.PI) / 180;

  const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
};

/**
 * Calculates total distance of a polyline
 * @param polyline Decoded polyline coordinates
 * @returns Total distance in meters
 */
export const calculatePolylineDistance = (polyline: google.maps.LatLngLiteral[]): number => {
  let totalDistance = 0;

  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < polyline.length - 1; i++) {
    totalDistance += getDistanceBetweenPoints(polyline[i], polyline[i + 1]);
  }

  return totalDistance;
};

/**
 * Gets a point at a specific distance along the polyline
 * Useful for placing markers at specific intervals
 *
 * @param polyline Decoded polyline coordinates
 * @param targetDistance Target distance in meters from start
 * @returns Coordinate at the target distance
 */
export const getPointAtDistance = (
  polyline: google.maps.LatLngLiteral[],
  targetDistance: number,
): google.maps.LatLngLiteral | null => {
  let accumulatedDistance = 0;

  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < polyline.length - 1; i++) {
    const segmentDistance = getDistanceBetweenPoints(polyline[i], polyline[i + 1]);

    if (accumulatedDistance + segmentDistance >= targetDistance) {
      // Target is on this segment
      const remainingDistance = targetDistance - accumulatedDistance;
      const ratio = remainingDistance / segmentDistance;

      // Linear interpolation between two points
      return {
        lat: polyline[i].lat + (polyline[i + 1].lat - polyline[i].lat) * ratio,
        lng: polyline[i].lng + (polyline[i + 1].lng - polyline[i].lng) * ratio,
      };
    }

    accumulatedDistance += segmentDistance;
  }

  // If target distance exceeds polyline length, return last point
  return polyline.length > 0 ? polyline[polyline.length - 1] : null;
};

/**
 * Samples a path to a maximum number of segments for animation
 * Ensures consistent animation duration regardless of path length
 *
 * For long routes with thousands of points, this reduces the path to a manageable
 * number of segments, allowing for smooth, constant-duration animations.
 *
 * @param path Array of LatLng coordinates
 * @param maxSegments Maximum number of segments to return (default: 150)
 * @returns Sampled path with at most maxSegments points
 *
 * @example
 * // Long route with 45,000 points
 * const sampled = samplePathForAnimation(longPath, 150);
 * // Returns 150 evenly-spaced points
 * // Animation: 3000ms / 150 = 20ms per segment = smooth 50 FPS
 */
export const samplePathForAnimation = (
  path: google.maps.LatLngLiteral[],
  maxSegments: number = 150,
): google.maps.LatLngLiteral[] => {
  // If path is already short enough, return as-is
  if (path.length <= maxSegments) {
    return path;
  }

  const sampledPath: google.maps.LatLngLiteral[] = [];

  // Calculate step size to evenly sample the path
  const step = (path.length - 1) / (maxSegments - 1);

  // Sample points evenly from the original path
  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < maxSegments - 1; i++) {
    const index = Math.round(i * step);
    sampledPath.push(path[index]);
  }

  // Always include the last point to ensure complete route
  sampledPath.push(path[path.length - 1]);

  return sampledPath;
};
